import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import moment from 'moment';
import { t } from '@lingui/macro';
import {
  ListItem,
  ListItemContainer,
  CheckboxWrapper,
  ContextMenuAction,
  StudentDetailsRow,
  StudentDetails,
  StudentName,
  StudentGroup
} from './styled';
import Checkbox from '../checkbox';
import { CHECKED, UNCHECKED, INDETERMINATE } from '../checkbox/constants';
import {
  getTruncatedName,
  getContextCoords,
  MAX_CLASSNAME_LENGTH,
  MAX_GROUP_NAME_LENGTH,
  isDefined
} from '../../helpers/utils';
import ContextModal, { ContextModalWidth } from '../context-modal';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import {
  Select,
  Results,
  AllAssigned,
  Unassign
} from '../../helpers/icon-dictionary';
import {
  ERROR_DETECTED,
  GRID_CELL_SELECTION
} from '../../containers/course-assign/course-assign-types';
import {
  getTopics,
  getStudentResultById
} from '../../containers/course-assign/course-assign-actions';
import Resources from '../../helpers/resources';
import { showToast } from '../toast/toast-action';
import { activateModal } from '../../containers/modal-manager/modal-manager-actions';
import { viewStudentResultModal, TABS } from './student-modal/student-modal';
import { useTracking } from '../../analytics';

const StudentListItem = (props) => {
  const {
    student,
    mappedAssignmentIndexes,
    showCheckbox,
    i18n,
    dueDate
  } = props;
  const studentListRef = useRef(null);
  const dispatch = useDispatch();
  const { trackStudentMenu } = useTracking();
  const [checkboxState, setCheckboxState] = useState(UNCHECKED);
  const [showContextMenu, setContextMenu] = useState(false);
  const [contextPosition, setContextPosition] = useState({
    positionX: 0,
    positionY: 0,
    positionArrow: 0,
    positionArrowY: 0
  });

  const fullName = `${student.lastName}, ${student.firstName}`;

  const classroomId = useSelector(
    (state) => state.CourseAssignReducer.activeClass
  );

  const groupId = useSelector((state) => state.CourseAssignReducer.activeGroup);

  const studentInfo = useSelector(
    (state) =>
      mappedAssignmentIndexes.map(
        (x) => state.CourseAssignReducer.assignments.studentAssignments[x]
      ),
    shallowEqual
  );

  const handleOnClick = (cbStatus) => {
    setCheckboxState(cbStatus);
    dispatch({
      type: GRID_CELL_SELECTION,
      indexes: mappedAssignmentIndexes,
      status: cbStatus
    });
    setContextMenu(false);
  };

  function handleSuccess() {
    dispatch(getTopics(classroomId, groupId));
    setContextMenu(false);
  }

  const handleOnUnassign = () => {
    const assignedTaskIdList = studentInfo.map(
      (info) => info.assignment?.assignedTaskId
    );

    Resources.unassign
      .put({ classroomId, groupId })
      .withBody({
        assignedTaskIdList
      })
      .send()
      .then(
        () => {
          handleSuccess();
          dispatch(
            showToast(
              i18n._(
                t(
                  'studentListItem.unassignSuccess'
                )`Activities are unassigned from this student`
              ),
              'success'
            )
          );
        },
        (err) => {
          dispatch({
            type: ERROR_DETECTED,
            payload: err
          });
        }
      );
  };

  function filterActivityList() {
    const activities = [];
    const selectedActivities = [];
    const filteredActivities =
      (studentInfo.length &&
        studentInfo?.filter((selectedActivity) => {
          if (
            isDefined(selectedActivity) &&
            !activities.includes(
              `${selectedActivity.activityId}_${selectedActivity.taskType}`
            ) &&
            !selectedActivity.isHidden
          ) {
            activities.push(
              `${selectedActivity.activityId}_${selectedActivity.taskType}`
            );
            if (selectedActivity.isSelected) {
              selectedActivities.push(selectedActivity);
            }
            return true;
          }
          return false;
        })) ||
      [];
    return [filteredActivities, selectedActivities];
  }

  const getState = () => {
    const [filteredActivities, selectedCells] = filterActivityList();
    let state = checkboxState;

    if (!selectedCells.length && !filteredActivities.length) state = UNCHECKED;
    if (selectedCells.length >= filteredActivities.length) {
      state = CHECKED;
    }
    if (
      selectedCells.length > 0 &&
      selectedCells.length < filteredActivities.length
    ) {
      state = INDETERMINATE;
    }
    if (selectedCells.length === 0) state = UNCHECKED;

    return state;
  };
  // TODO: refactor getState to remove the exhaustive-deps warning
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setCheckboxState(getState());
  });

  const showContextModal = () => {
    const { posX, posY, posArrow, posArrowY } = getContextCoords(
      studentListRef.current.getBoundingClientRect(),
      ContextModalWidth,
      300,
      true
    );
    setContextPosition({
      positionX: posX,
      positionY: posY,
      positionArrow: posArrow,
      positionArrowY: posArrowY
    });
    setContextMenu(!showContextMenu);
  };

  const openStudentModal = (tabToOpen) => {
    dispatch(getStudentResultById(classroomId, student.encryptedUserProfileId));
    dispatch(activateModal(viewStudentResultModal(i18n, tabToOpen)));
  };

  const { id = '' } = student;
  const classname =
    student.className.length > MAX_CLASSNAME_LENGTH
      ? `${student.className.substring(0, MAX_CLASSNAME_LENGTH)}...`
      : student.className;

  const groupname =
    student.groupName.length > MAX_GROUP_NAME_LENGTH
      ? `${student.groupName.substring(0, MAX_GROUP_NAME_LENGTH)}...`
      : student.groupName;

  return (
    <ListItemContainer key={`_${id}_`} data-name="student-list">
      <ListItem
        data-testid="student-list"
        showCheckbox={showCheckbox}
        onClick={showContextModal}
        ref={studentListRef}
      >
        <span title={fullName}>{getTruncatedName(fullName)}</span>
        <span id="assignedActivity">{props.assignmentCount}</span>
      </ListItem>
      <CheckboxWrapper>
        {showCheckbox === true && (
          <Checkbox
            checkboxHandler={(cbStatus) => handleOnClick(cbStatus)}
            state={checkboxState}
          />
        )}
      </CheckboxWrapper>
      <OutsideClickHandler
        callback={() => setContextMenu(false)}
        forButton={`showContextModal_${id}`}
      >
        <ContextModal
          show={showContextMenu}
          leftAlign
          flowUp
          {...contextPosition}
        >
          <StudentDetails data-testid="student-details">
            <StudentName data-testid="student-name">
              {getTruncatedName(fullName)}
            </StudentName>
            <StudentGroup data-testid="student-year">
              {`${i18n._(t('StudentListItem.year')`Year`)} ${
                student.yearGroup
              } ${classname}`}
            </StudentGroup>
            <StudentDetailsRow data-testid="student-group">
              {`${i18n._(t('StudentListItem.group')`Group`)}: ${groupname}`}
            </StudentDetailsRow>
            <StudentDetailsRow data-testid="student-assignment">
              {`${i18n._(
                t('StudentListItem.courseAssignments')`Course Assignments`
              )}: ${props.assignmentCount}`}
            </StudentDetailsRow>
            {isDefined(dueDate) && (
              <StudentDetailsRow data-testid="next-due">
                {`${i18n._(t('StudentListItem.nextDue')`Next Due`)}: ${moment(
                  dueDate
                ).format('DD MMM YYYY')}`}
              </StudentDetailsRow>
            )}
          </StudentDetails>
          <ContextMenuAction
            data-testid="select-student"
            role="button"
            tabIndex="-1"
            first
            onClick={() => {
              trackStudentMenu({ action: 'select' });
              handleOnClick('checked');
            }}
          >
            <Isvg src={Select} cacheGetRequests />
            {i18n._(t('StudentListItem.SelectStudent')`Select Student`)}
          </ContextMenuAction>
          <ContextMenuAction
            data-testid="unassign-all"
            role="button"
            tabIndex="-1"
            onClick={() => {
              trackStudentMenu({ action: 'unassign-all' });
              handleOnUnassign();
            }}
          >
            <Isvg src={Unassign} cacheGetRequests />
            {i18n._(t('StudentListItem.UnassignAll')`Unassign All`)}
          </ContextMenuAction>
          <ContextMenuAction
            data-testid="student-results"
            role="button"
            tabIndex="-1"
            onClick={() => {
              trackStudentMenu({ action: 'results' });
              openStudentModal(TABS.RESULTS);
            }}
          >
            <Isvg src={Results} cacheGetRequests />
            {i18n._(t('StudentListItem.StudentResults')`Student Results`)}
          </ContextMenuAction>
          <ContextMenuAction
            data-testid="current-assignments"
            role="button"
            tabIndex="-1"
            onClick={() => {
              trackStudentMenu({ action: 'current-assignments' });
              openStudentModal(TABS.ASSIGNED);
            }}
            last
          >
            <Isvg src={AllAssigned} cacheGetRequests />
            {i18n._(
              t('StudentListItem.CurrentAssignments')`Current Assignments`
            )}
          </ContextMenuAction>
        </ContextModal>
      </OutsideClickHandler>
    </ListItemContainer>
  );
};

StudentListItem.defaultProps = {
  showCheckbox: true,
  assignmentCount: 0,
  dueDate: undefined
};

StudentListItem.propTypes = {
  student: PropTypes.object,
  mappedAssignmentIndexes: PropTypes.arrayOf(PropTypes.number).isRequired,
  checkboxHandler: PropTypes.func,
  showCheckbox: PropTypes.bool,
  assignmentCount: PropTypes.number,
  dueDate: PropTypes.string,
  i18n: PropTypes.object,
  showModal: PropTypes.func
};

export default StudentListItem;



// WEBPACK FOOTER //
// ./components/student-list/student-list-item.js